import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const Applications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!currentUser) return;

    const loadApplications = async () => {
      try {
        setLoading(true);
        setError('');
        
        const userId = currentUser._id || currentUser.uid;
        const response = await ApiService.getApplicationsByUser(userId);
        setApplications(response.applications || []);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setError('Failed to load applications. Please try again later.');
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    loadApplications();
  }, [currentUser]);

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedApplication(null);
    setFollowUpMessage('');
  };

  const handleDeleteApplication = async (id) => {
    try {
      await ApiService.withdrawApplication(id);
      setApplications(applications.filter(app => app._id !== id));
      setSnackbar({
        open: true,
        message: 'Application withdrawn successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error withdrawing application:', error);
      setSnackbar({
        open: true,
        message: 'Failed to withdraw application',
        severity: 'error'
      });
    }
  };

  const handleSendFollowUp = async () => {
    try {
      // This would typically be an API call to send a follow-up message
      console.log('Sending follow-up for application:', selectedApplication._id);
      console.log('Message:', followUpMessage);
      
      setSnackbar({
        open: true,
        message: 'Follow-up message sent successfully',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (error) {
      console.error('Error sending follow-up:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send follow-up message',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'default';
      case 'under_review':
        return 'info';
      case 'shortlisted':
        return 'primary';
      case 'interview_scheduled':
        return 'warning';
      case 'interviewed':
        return 'secondary';
      case 'offer_extended':
        return 'success';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'withdrawn':
        return 'default';
      default:
        return 'default';
    }
  };

  const statusOrder = [
    'submitted',
    'under_review',
    'shortlisted',
    'interview_scheduled',
    'interviewed',
    'offer_extended',
    'accepted',
    'rejected',
    'withdrawn'
  ];

  const statusLabels = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    shortlisted: 'Shortlisted',
    interview_scheduled: 'Interview Scheduled',
    interviewed: 'Interviewed',
    offer_extended: 'Offer Extended',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn'
  };

  const columns = [
    { 
      field: 'jobTitle', 
      headerName: 'Job Title', 
      flex: 1,
      renderCell: (params) => params.row.job?.title || 'N/A'
    },
    { 
      field: 'companyName', 
      headerName: 'Company', 
      flex: 1,
      renderCell: (params) => params.row.job?.companyName || 'N/A'
    },
    { 
      field: 'location', 
      headerName: 'Location', 
      width: 150,
      renderCell: (params) => {
        const location = params.row.job?.location;
        return location ? `${location.city}, ${location.country}` : 'N/A';
      }
    },
    { 
      field: 'jobType', 
      headerName: 'Job Type', 
      width: 120,
      renderCell: (params) => params.row.job?.jobType || 'N/A'
    },
    { 
      field: 'appliedDate', 
      headerName: 'Applied Date', 
      width: 120,
      renderCell: (params) => new Date(params.row.createdAt).toLocaleDateString()
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleViewDetails(params.row)}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Withdraw Application">
            <IconButton
              onClick={() => handleDeleteApplication(params.row._id)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Transform applications data for DataGrid (add id field)
  const applicationsWithId = applications.map(app => ({
    ...app,
    id: app._id // DataGrid requires an 'id' field
  }));

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Applications
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            {applicationsWithId.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No applications found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You haven't applied to any jobs yet.
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={applicationsWithId}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                autoHeight
                disableSelectionOnClick
                getRowId={(row) => row._id}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: 'none',
                  },
                  '& .MuiDataGrid-row': {
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedApplication && (
          <>
            <DialogTitle>
              Application Details - {selectedApplication.job?.title || 'N/A'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Company
                </Typography>
                <Typography gutterBottom>{selectedApplication.job?.companyName || 'N/A'}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Location
                </Typography>
                <Typography gutterBottom>
                  {selectedApplication.job?.location ? 
                    `${selectedApplication.job.location.city}, ${selectedApplication.job.location.country}` : 
                    'N/A'
                  }
                </Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Job Type
                </Typography>
                <Typography gutterBottom>{selectedApplication.job?.jobType || 'N/A'}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Applied Date
                </Typography>
                <Typography gutterBottom>
                  {new Date(selectedApplication.createdAt).toLocaleDateString()}
                </Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={statusLabels[selectedApplication.status] || selectedApplication.status}
                  color={getStatusColor(selectedApplication.status)}
                />

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {statusOrder.map((s) => (
                    <Chip
                      key={s}
                      label={statusLabels[s]}
                      color={s === selectedApplication.status ? getStatusColor(s) : 'default'}
                      variant={s === selectedApplication.status ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
                </Box>

                {selectedApplication.coverLetter && (
                  <>
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ mt: 2 }}>
                      Cover Letter
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApplication.coverLetter}
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Send Follow-up Message
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type your follow-up message here..."
                  value={followUpMessage}
                  onChange={(e) => setFollowUpMessage(e.target.value)}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button
                onClick={handleSendFollowUp}
                variant="contained"
                startIcon={<SendIcon />}
                disabled={!followUpMessage.trim()}
              >
                Send Follow-up
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Applications;